// ─── BingoGamePage.jsx ────────────────────────────────────────────────────────
// kazna Bingo — Live Game Screen  (complete redesign)
// Layout:  single scrollable column, phone-optimised, scales to desktop
// Sections: Header → StatBar → CallingHero → PrevNumbers → RefBoard → MyCard → Footer
// Logic:
//   • Auto ON  → card marks itself; BINGO btn disabled
//   • Auto OFF → player taps called cells; BINGO btn enabled when pattern complete
// No Tailwind. Tokens from theme.js.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef, useCallback } from "react";
import {
  Flame,
  Bell,
  Settings,
  Hash,
  Users,
  Coins,
  Trophy,
  Zap,
  LogOut,
  RefreshCw,
  Volume2,
  VolumeX,
  TrendingUp,
} from "lucide-react";
import socketService from "../services/socket";
import { useGame } from "../contexts/GameContext";
import { useAuth } from "../contexts/AuthContext"; // ADD this import
import { theme, injectTheme } from "../assets/theme";

// ─── Data ─────────────────────────────────────────────────────────────────────

const COLS = ["B", "I", "N", "G", "O"];

// 5×5 card — 0 = FREE center
const BLANK_GRID = [
  [0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0],
];

// ─── Bingo check ──────────────────────────────────────────────────────────────
function countLines(grid, marked) {
  const m = (r, c) => grid[r][c] === 0 || marked.has(grid[r][c]);
  let lines = 0;
  for (let r = 0; r < 5; r++)
    if ([0, 1, 2, 3, 4].every((c) => m(r, c))) lines++;
  for (let c = 0; c < 5; c++)
    if ([0, 1, 2, 3, 4].every((r) => m(r, c))) lines++;
  if ([0, 1, 2, 3, 4].every((i) => m(i, i))) lines++;
  if ([0, 1, 2, 3, 4].every((i) => m(i, 4 - i))) lines++;
  // Four corners
  if (m(0, 0) && m(0, 4) && m(4, 0) && m(4, 4)) lines++;
  return lines;
}
function hasBingo(grid, marked, requiredLines = 1) {
  return countLines(grid, marked) >= requiredLines;
}
// hasBingo now takes the grid as a parameter instead of closing over CARD:
// function hasBingo(grid, marked) {
//   const m = (r, c) => grid[r][c] === 0 || marked.has(grid[r][c]);
//   for (let r = 0; r < 5; r++)
//     if ([0, 1, 2, 3, 4].every((c) => m(r, c))) return true;
//   for (let c = 0; c < 5; c++)
//     if ([0, 1, 2, 3, 4].every((r) => m(r, c))) return true;
//   if ([0, 1, 2, 3, 4].every((i) => m(i, i))) return true;
//   if ([0, 1, 2, 3, 4].every((i) => m(i, 4 - i))) return true;
//   return false;
// }

// ─── Style injection ──────────────────────────────────────────────────────────

function injectStyles() {
  if (document.getElementById("bg-styles")) return;
  const s = document.createElement("style");
  s.id = "bg-styles";
  s.textContent = `
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: ${theme.color.bgBase}; -webkit-font-smoothing: antialiased; }

    @keyframes numPop {
      0%   { transform: scale(0.3) rotate(-12deg); opacity:0; }
      65%  { transform: scale(1.22) rotate(3deg);  opacity:1; }
      100% { transform: scale(1)    rotate(0);      opacity:1; }
    }
    @keyframes ringBreath {
      0%,100% { box-shadow: 0 0 0 0 rgba(249,115,22,0.6), 0 0 50px rgba(249,115,22,0.2); }
      50%      { box-shadow: 0 0 0 16px rgba(249,115,22,0), 0 0 80px rgba(249,115,22,0.5); }
    }
    @keyframes markBounce {
      0%   { transform: scale(0.6); }
      55%  { transform: scale(1.2); }
      100% { transform: scale(1);   }
    }
    @keyframes slideLeft {
      from { transform: translateX(24px); opacity:0; }
      to   { transform: translateX(0);    opacity:1; }
    }
    @keyframes bingoGlow {
      0%,100% { box-shadow: 0 0 20px rgba(249,115,22,0.6); }
      50%      { box-shadow: 0 0 50px rgba(249,115,22,1);   }
    }
    @keyframes refAppear {
      from { opacity:0; transform: scale(0.85); }
      to   { opacity:1; transform: scale(1);    }
    }

    .bingo-pulse { animation: bingoGlow 1.4s ease infinite; }

    @keyframes winPop {
      0%   { transform: scale(0.55) translateY(30px); opacity: 0; }
      65%  { transform: scale(1.04) translateY(-6px); opacity: 1; }
      100% { transform: scale(1)    translateY(0);    opacity: 1; }
    }
    @keyframes winBdIn  { from { opacity:0; backdrop-filter:blur(0px); } to { opacity:1; backdrop-filter:blur(10px); } }
    @keyframes trophyFloat {
      0%,100% { transform: translateY(0) scale(1);    filter: drop-shadow(0 0 12px rgba(249,115,22,0.7)); }
      50%     { transform: translateY(-8px) scale(1.06); filter: drop-shadow(0 0 26px rgba(249,115,22,1));   }
    }
    @keyframes countdownBar {
      from { width: 100%; }
      to   { width: 0%;   }
    }
    @keyframes prizeShine {
      0%   { background-position: -200% center; }
      100% { background-position:  200% center; }
    }
    @keyframes dlgIn  { 0%{transform:scale(0.88) translateY(14px);opacity:0} 65%{transform:scale(1.03) translateY(-3px);opacity:1} 100%{transform:scale(1) translateY(0);opacity:1} }
    @keyframes bdIn   { from{opacity:0} to{opacity:1} }
    .dlg-box  { animation: dlgIn .30s cubic-bezier(.34,1.56,.64,1) both; }
    .dlg-back { animation: bdIn  .20s ease both; }
    .trophy-float { animation: trophyFloat 2s ease-in-out infinite; }

    /* ref board cell */
    .rb { transition: all 0.22s ease; }
    .rb:hover { transform: scale(1.15); z-index:2; }

    /* card cell */
    .cc { transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease; }
    .cc.tappable:hover { transform: scale(1.08); z-index:2; }

    /* hide scrollbar */
    .noscroll::-webkit-scrollbar { display:none; }
    .noscroll { -ms-overflow-style:none; scrollbar-width:none; }
  `;
  document.head.appendChild(s);
}

// ─── StatPill ─────────────────────────────────────────────────────────────────

function StatPill({ icon: Icon, label, value, accent }) {
  return (
    <div
      style={{
        flex: "1 1 0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "2px",
        padding: "8px 6px",
        background: "rgba(255,255,255,0.04)",
        border: `1px solid ${theme.color.borderCard}`,
        borderRadius: theme.radius.md,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
        <Icon size={10} color={accent ?? theme.color.accentLight} />
        <span
          style={{
            fontFamily: theme.font.bodyFamily,
            fontSize: "9px",
            fontWeight: 600,
            color: theme.color.textDim,
            textTransform: "uppercase",
            letterSpacing: "0.14em",
          }}
        >
          {label}
        </span>
      </div>
      <span
        style={{
          fontFamily: theme.font.family,
          fontWeight: 900,
          fontSize: "15px",
          color: theme.color.textPrimary,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1,
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Amharic number words (1–75) ─────────────────────────────────────────────

const AM_WORDS = {
  1: "አንድ",
  2: "ሁለት",
  3: "ሶስት",
  4: "አራት",
  5: "አምስት",
  6: "ስድስት",
  7: "ሰባት",
  8: "ስምንት",
  9: "ዘጠኝ",
  10: "አስር",
  11: "አስራ አንድ",
  12: "አስራ ሁለት",
  13: "አስራ ሶስት",
  14: "አስራ አራት",
  15: "አስራ አምስት",
  16: "አስራ ስድስት",
  17: "አስራ ሰባት",
  18: "አስራ ስምንት",
  19: "አስራ ዘጠኝ",
  20: "ሃያ",
  21: "ሃያ አንድ",
  22: "ሃያ ሁለት",
  23: "ሃያ ሶስት",
  24: "ሃያ አራት",
  25: "ሃያ አምስት",
  26: "ሃያ ስድስት",
  27: "ሃያ ሰባት",
  28: "ሃያ ስምንት",
  29: "ሃያ ዘጠኝ",
  30: "ሰላሳ",
  31: "ሰላሳ አንድ",
  32: "ሰላሳ ሁለት",
  33: "ሰላሳ ሶስት",
  34: "ሰላሳ አራት",
  35: "ሰላሳ አምስት",
  36: "ሰላሳ ስድስት",
  37: "ሰላሳ ሰባት",
  38: "ሰላሳ ስምንት",
  39: "ሰላሳ ዘጠኝ",
  40: "አርባ",
  41: "አርባ አንድ",
  42: "አርባ ሁለት",
  43: "አርባ ሶስት",
  44: "አርባ አራት",
  45: "አርባ አምስት",
  46: "አርባ ስድስት",
  47: "አርባ ሰባት",
  48: "አርባ ስምንት",
  49: "አርባ ዘጠኝ",
  50: "ሃምሳ",
  51: "ሃምሳ አንድ",
  52: "ሃምሳ ሁለት",
  53: "ሃምሳ ሶስት",
  54: "ሃምሳ አራት",
  55: "ሃምሳ አምስት",
  56: "ሃምሳ ስድስት",
  57: "ሃምሳ ሰባት",
  58: "ሃምሳ ስምንት",
  59: "ሃምሳ ዘጠኝ",
  60: "ስልሳ",
  61: "ስልሳ አንድ",
  62: "ስልሳ ሁለት",
  63: "ስልሳ ሶስት",
  64: "ስልሳ አራት",
  65: "ስልሳ አምስት",
  66: "ስልሳ ስድስት",
  67: "ስልሳ ሰባት",
  68: "ስልሳ ስምንት",
  69: "ስልሳ ዘጠኝ",
  70: "ሰባ",
  71: "ሰባ አንድ",
  72: "ሰባ ሁለት",
  73: "ሰባ ሶስት",
  74: "ሰባ አራት",
  75: "ሰባ አምስት",
};

// ─── CallingHero ──────────────────────────────────────────────────────────────

function CallingHero({ current, voiceOn, onToggleVoice }) {
  const [key, setKey] = useState(0);
  const prev = useRef(current);
  useEffect(() => {
    if (current !== prev.current) {
      setKey((k) => k + 1);
      prev.current = current;
    }
  }, [current]);

  const colLetter =
    current != null
      ? current <= 15
        ? "B"
        : current <= 30
          ? "I"
          : current <= 45
            ? "N"
            : current <= 60
              ? "G"
              : "O"
      : null;
  const amWord =
    current != null ? (AM_WORDS[current] ?? String(current)) : null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "10px",
      }}
    >
      {/* Rings + dial */}
      <div
        style={{
          position: "relative",
          width: "164px",
          height: "164px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Ring 3 – faintest */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            border: "1px solid rgba(249,115,22,0.10)",
          }}
        />
        {/* Ring 2 */}
        <div
          style={{
            position: "absolute",
            inset: "12px",
            borderRadius: "50%",
            border: "1px solid rgba(249,115,22,0.18)",
          }}
        />
        {/* Main glowing ring */}
        <div
          style={{
            position: "absolute",
            inset: "24px",
            borderRadius: "50%",
            background: "radial-gradient(circle at 38% 32%, #201808, #0d0a05)",
            border: `2px solid ${theme.color.accentRing}`,
            animation: "ringBreath 2.4s ease infinite",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "0",
          }}
        >
          {/* CALLING label */}
          <span
            style={{
              fontFamily: theme.font.bodyFamily,
              fontWeight: 600,
              fontSize: "9px",
              color: theme.color.textDim,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              marginBottom: "1px",
            }}
          >
            Calling
          </span>

          {/* Column letter */}
          {colLetter && (
            <span
              style={{
                fontFamily: theme.font.family,
                fontWeight: 700,
                fontSize: "13px",
                color: theme.color.accentLight,
                letterSpacing: "0.20em",
                lineHeight: 1,
                marginBottom: "0px",
              }}
            >
              {colLetter}
            </span>
          )}

          {/* The number */}
          <span
            key={key}
            style={{
              fontFamily: theme.font.family,
              fontWeight: 900,
              fontSize: "46px",
              color: "#fff",
              lineHeight: 1,
              letterSpacing: "-0.02em",
              textShadow: `0 0 30px ${theme.color.accentGlow}`,
              animation: "numPop 0.44s cubic-bezier(0.34,1.56,0.64,1) both",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {current ?? "—"}
          </span>

          {/* Accent underbar */}
          <span
            style={{
              width: "28px",
              height: "3px",
              borderRadius: "9999px",
              background: theme.color.accent,
              boxShadow: `0 0 10px ${theme.color.accentGlow}`,
              marginTop: "4px",
            }}
          />
        </div>

        {/* Mute toggle — bottom-right of the ring */}
        <button
          onClick={onToggleVoice}
          aria-label={voiceOn ? "Mute voice caller" : "Unmute voice caller"}
          title={voiceOn ? "Mute" : "Unmute"}
          style={{
            position: "absolute",
            bottom: "4px",
            right: "4px",
            width: "28px",
            height: "28px",
            borderRadius: "50%",
            background: voiceOn
              ? theme.color.accentMuted
              : "rgba(255,255,255,0.08)",
            border: `1px solid ${voiceOn ? theme.color.accentRing : theme.color.borderCard}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: theme.transition.base,
            zIndex: 10,
            boxShadow: voiceOn ? "0 0 8px rgba(249,115,22,0.45)" : "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          {voiceOn ? (
            <Volume2 size={13} color={theme.color.accentLight} />
          ) : (
            <VolumeX size={13} color={theme.color.textMuted} />
          )}
        </button>
      </div>

      {/* Amharic word — shown below the ring */}
      <div
        style={{
          minHeight: "28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {amWord && (
          <span
            key={`w-${key}`}
            style={{
              fontFamily: theme.font.family,
              fontWeight: 700,
              fontSize: "clamp(16px,4vw,22px)",
              color: theme.color.accentLight,
              letterSpacing: "0.04em",
              textShadow: `0 0 14px rgba(249,115,22,0.50)`,
              animation: "numPop 0.44s cubic-bezier(0.34,1.56,0.64,1) both",
              animationDelay: "0.08s",
            }}
          >
            {amWord}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── PreviousStrip ────────────────────────────────────────────────────────────

function PreviousStrip({ history, current }) {
  // show last 8 called, newest first, excluding current
  const prev = history.slice(0, -1).slice(-8).reverse();
  return (
    <div style={{ padding: "0 16px" }}>
      <span
        style={{
          fontFamily: theme.font.bodyFamily,
          fontSize: "9px",
          fontWeight: 600,
          color: theme.color.textDim,
          textTransform: "uppercase",
          letterSpacing: "0.18em",
          display: "block",
          marginBottom: "8px",
        }}
      >
        Previous Numbers
      </span>
      <div
        className="noscroll"
        style={{
          display: "flex",
          gap: "8px",
          overflowX: "auto",
          paddingBottom: "2px",
        }}
      >
        {prev.length === 0 && (
          <span
            style={{
              fontFamily: theme.font.bodyFamily,
              fontSize: "11px",
              color: theme.color.textFaint,
            }}
          >
            —
          </span>
        )}
        {prev.map((n, i) => (
          <div
            key={`${n}-${i}`}
            style={{
              flexShrink: 0,
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background:
                i === 0 ? "rgba(249,115,22,0.18)" : "rgba(255,255,255,0.05)",
              border: `1px solid ${i === 0 ? theme.color.accentRing : theme.color.borderCard}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: theme.font.family,
              fontWeight: 700,
              fontSize: "14px",
              color: i === 0 ? theme.color.accentLight : theme.color.textMuted,
              animation: "slideLeft 0.28s ease both",
              animationDelay: `${i * 0.04}s`,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {n}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── ReferenceBoard ───────────────────────────────────────────────────────────

function ReferenceBoard({ called }) {
  const nums = Array.from({ length: 75 }, (_, i) => i + 1);
  return (
    <div
      style={{
        margin: "0 16px",
        background: "rgba(255,255,255,0.02)",
        border: `1px solid ${theme.color.borderCard}`,
        borderRadius: theme.radius.lg,
        padding: "12px",
      }}
    >
      {/* header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Hash size={12} color={theme.color.accentLight} />
          <span
            style={{
              fontFamily: theme.font.family,
              fontWeight: 900,
              fontSize: "12px",
              color: theme.color.textPrimary,
              textTransform: "uppercase",
              letterSpacing: "0.10em",
            }}
          >
            Reference Board{" "}
            <span
              style={{
                color: theme.color.textDim,
                fontWeight: 400,
                fontSize: "10px",
              }}
            >
              (1–75)
            </span>
          </span>
        </div>
        <span
          style={{
            fontFamily: theme.font.bodyFamily,
            fontSize: "9px",
            fontWeight: 700,
            color: theme.color.accentLight,
            background: theme.color.accentMuted,
            border: `1px solid ${theme.color.accentRing}`,
            borderRadius: "9999px",
            padding: "2px 8px",
          }}
        >
          Live Sync
        </span>
      </div>

      {/* grid 15 cols */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(15,1fr)",
          gap: "3px",
        }}
      >
        {nums.map((n) => {
          const hit = called.has(n);
          return (
            <div
              key={n}
              className="rb"
              style={{
                aspectRatio: "1",
                borderRadius: "4px",
                background: hit
                  ? `linear-gradient(135deg,${theme.color.playFrom},${theme.color.playTo})`
                  : "rgba(255,255,255,0.04)",
                border: `1px solid ${hit ? theme.color.accent : theme.color.borderCard}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: theme.font.family,
                fontWeight: hit ? 900 : 500,
                fontSize: "clamp(7px,1.1vw,10px)",
                color: hit ? "#fff" : theme.color.textDim,
                boxShadow: hit ? `0 0 5px rgba(249,115,22,0.45)` : "none",
                cursor: "default",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {n}
            </div>
          );
        })}
      </div>

      {/* legend */}
      <div style={{ display: "flex", gap: "14px", marginTop: "8px" }}>
        {[
          { dot: theme.color.accent, label: "Called" },
          { dot: "rgba(255,255,255,0.25)", label: "Not called" },
        ].map(({ dot, label }) => (
          <div
            key={label}
            style={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            <span
              style={{
                width: "7px",
                height: "7px",
                borderRadius: "50%",
                background: dot,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontFamily: theme.font.bodyFamily,
                fontSize: "10px",
                color: theme.color.textDim,
              }}
            >
              {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── BingoCard ────────────────────────────────────────────────────────────────

function BingoCard({ called, isAuto, userMarked, onMark, grid }) {
  return (
    <div
      style={{
        margin: "0 16px",
        background: "rgba(255,255,255,0.025)",
        border: `1px solid ${theme.color.borderCard}`,
        borderRadius: theme.radius.lg,
        padding: "12px",
      }}
    >
      {/* Col headers */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5,1fr)",
          gap: "5px",
          marginBottom: "5px",
        }}
      >
        {COLS.map((c) => (
          <div
            key={c}
            style={{
              textAlign: "center",
              fontFamily: theme.font.family,
              fontWeight: 900,
              fontSize: "18px",
              color: theme.color.accentLight,
              letterSpacing: "0.06em",
              padding: "2px 0",
            }}
          >
            {c}
          </div>
        ))}
      </div>

      {/* Rows */}
      {grid.map((row, ri) => (
        <div
          key={ri}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5,1fr)",
            gap: "5px",
            marginBottom: ri < 4 ? "5px" : 0,
          }}
        >
          {row.map((num, ci) => {
            const free = num === 0;
            const isCalled = free || called.has(num);
            const isMarked = free || (isAuto ? isCalled : userMarked.has(num));
            const canTap = !isAuto && !free && isCalled && !isMarked;

            let bg, border, color, shadow, anim;
            if (free) {
              bg = "linear-gradient(135deg,#162850,#0f1d3a)";
              border = "#3b82f6";
              color = "#93c5fd";
              shadow = "0 0 12px rgba(59,130,246,0.5)";
              anim = "";
            } else if (isMarked) {
              bg = `linear-gradient(135deg,${theme.color.playFrom},${theme.color.playTo})`;
              border = theme.color.accent;
              color = "#fff";
              shadow = `0 0 14px rgba(249,115,22,0.65)`;
              anim = "markBounce 0.4s cubic-bezier(0.34,1.56,0.64,1) both";
            } else if (canTap) {
              bg = "rgba(249,115,22,0.09)";
              border = theme.color.accentRing;
              color = theme.color.accentLight;
              shadow = "none";
              anim = "";
            } else {
              bg = "rgba(255,255,255,0.04)";
              border = theme.color.borderCard;
              color = theme.color.textMuted;
              shadow = "none";
              anim = "";
            }

            return (
              <button
                key={`${ri}-${ci}`}
                className={`cc${canTap ? " tappable" : ""}`}
                onClick={() => canTap && onMark(num)}
                style={{
                  width: "100%",
                  aspectRatio: "2",
                  borderRadius: "10px",
                  background: bg,
                  border: `1.5px solid ${border}`,
                  color,
                  cursor: canTap ? "pointer" : "default",
                  fontFamily: theme.font.family,
                  fontWeight: 900,
                  fontSize: "clamp(13px,3.5vw,20px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: shadow,
                  outline: "none",
                  animation: anim,
                  fontVariantNumeric: "tabular-nums",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {free ? <Zap size={18} color="#93c5fd" fill="#93c5fd" /> : num}

                {/* shimmer overlay for tappable cells */}
                {canTap && (
                  <span
                    style={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: "inherit",
                      background:
                        "radial-gradient(circle,rgba(249,115,22,0.15) 0%,transparent 70%)",
                      pointerEvents: "none",
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
function LeaveConfirmDialog({ open, onCancel, onConfirm, gameStake }) {
  const [visible, setVisible] = useState(false);
  const [anim, setAnim] = useState(false);

  useEffect(() => {
    if (open) {
      setVisible(true);
      requestAnimationFrame(() => setAnim(true));
    } else {
      setAnim(false);
      const t = setTimeout(() => setVisible(false), 240);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  if (!visible) return null;

  return (
    /* Backdrop */
    <div
      className="dlg-back"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel?.();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        background: anim ? "rgba(0,0,0,0.75)" : "rgba(0,0,0,0)",
        backdropFilter: anim ? "blur(8px)" : "blur(0px)",
        transition: "background 0.22s ease, backdrop-filter 0.22s ease",
      }}
    >
      {/* Dialog box */}
      <div
        className="dlg-box"
        style={{
          width: "100%",
          maxWidth: "320px",
          background: theme.color.bgSurface,
          border: `1px solid rgba(239,68,68,0.25)`,
          borderRadius: theme.radius.xl,
          overflow: "hidden",
          boxShadow:
            "0 32px 80px rgba(0,0,0,0.85), 0 0 0 1px rgba(239,68,68,0.12)",
        }}
      >
        {/* Top accent strip */}
        <div
          style={{
            height: "3px",
            background: "linear-gradient(90deg,#ef4444,#dc2626)",
            boxShadow: "0 0 14px rgba(239,68,68,0.60)",
          }}
        />

        {/* Body */}
        <div
          style={{
            padding: "28px 24px 24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
          }}
        >
          {/* Icon circle */}
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: theme.radius.full,
              background: "rgba(239,68,68,0.12)",
              border: "1px solid rgba(239,68,68,0.30)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 24px rgba(239,68,68,0.25)",
            }}
          >
            <LogOut size={24} color="#ef4444" />
          </div>

          {/* Text */}
          <div
            style={{
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
          >
            <h2
              style={{
                fontFamily: theme.font.family,
                fontWeight: 900,
                fontSize: "22px",
                color: theme.color.textPrimary,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
              }}
            >
              Leave Game?
            </h2>
            <p
              style={{
                fontFamily: theme.font.bodyFamily,
                fontWeight: 400,
                fontSize: "13px",
                color: theme.color.textMuted,
                lineHeight: 1.55,
              }}
            >
              You'll forfeit your current progress and entry fee. This action{" "}
              <span style={{ color: "#f87171", fontWeight: 600 }}>
                cannot be undone.
              </span>
            </p>
          </div>

          {/* Info pill */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.20)",
              borderRadius: theme.radius.md,
              padding: "8px 14px",
              width: "100%",
            }}
          >
            <Coins size={13} color="#f87171" style={{ flexShrink: 0 }} />
            <span
              style={{
                fontFamily: theme.font.bodyFamily,
                fontSize: "11px",
                fontWeight: 600,
                color: "#f87171",
              }}
            >
              Entry fee of {gameStake} will not be refunded
            </span>
          </div>

          {/* Buttons */}
          <div
            style={{
              display: "flex",
              gap: "10px",
              width: "100%",
              marginTop: "4px",
            }}
          >
            {/* Cancel — stays in game */}
            <button
              onClick={onCancel}
              style={{
                flex: 1,
                padding: "13px",
                borderRadius: theme.radius.md,
                background: "rgba(255,255,255,0.05)",
                border: `1px solid ${theme.color.borderCard}`,
                cursor: "pointer",
                fontFamily: theme.font.family,
                fontWeight: 700,
                fontSize: "14px",
                color: theme.color.textMuted,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                transition: theme.transition.base,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.09)";
                e.currentTarget.style.color = theme.color.textPrimary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                e.currentTarget.style.color = theme.color.textMuted;
              }}
            >
              Stay
            </button>

            {/* Confirm leave */}
            <button
              onClick={onConfirm}
              style={{
                flex: 1,
                padding: "13px",
                borderRadius: theme.radius.md,
                background: "linear-gradient(135deg,#ef4444,#dc2626)",
                border: "none",
                cursor: "pointer",
                fontFamily: theme.font.family,
                fontWeight: 900,
                fontSize: "14px",
                color: "#fff",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                boxShadow: "0 4px 20px rgba(239,68,68,0.40)",
                transition: theme.transition.base,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 4px 36px rgba(239,68,68,0.70)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 4px 20px rgba(239,68,68,0.40)";
                e.currentTarget.style.transform = "none";
              }}
            >
              <LogOut size={14} color="#fff" />
              Leave
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
// ─── WinPopup ─────────────────────────────────────────────────────────────────

function WinPopup({
  open,
  iWon = true,
  winnerName = "",
  prize = "0",
  coins = 0,
  xp = 0,
  onDone,
}) {
  const [visible, setVisible] = useState(false);
  const [anim, setAnim] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const cdRef = useRef(null);

  useEffect(() => {
    if (open) {
      setCountdown(3);
      setVisible(true);
      requestAnimationFrame(() => setAnim(true));
    } else {
      setAnim(false);
      const t = setTimeout(() => setVisible(false), 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Start 3-second countdown once popup is visible
  useEffect(() => {
    if (!open) return;
    cdRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(cdRef.current);
          onDone?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(cdRef.current);
  }, [open, onDone]);

  if (!visible) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        background: anim ? "rgba(0,0,0,0.82)" : "rgba(0,0,0,0)",
        backdropFilter: anim ? "blur(10px)" : "blur(0px)",
        transition: "background 0.28s ease, backdrop-filter 0.28s ease",
      }}
    >
      {/* Card */}
      <div
        style={{
          width: "100%",
          maxWidth: "340px",
          background:
            "linear-gradient(160deg, #1a1208 0%, #0e0c07 60%, #111118 100%)",
          border: `1px solid rgba(249,115,22,0.35)`,
          borderRadius: "28px",
          overflow: "hidden",
          boxShadow:
            "0 0 0 1px rgba(249,115,22,0.12), 0 40px 100px rgba(0,0,0,0.95)",
          animation: "winPop 0.48s cubic-bezier(0.34,1.56,0.64,1) both",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Top glow strip */}
        <div
          style={{
            width: "100%",
            height: "3px",
            background: `linear-gradient(90deg, transparent, ${theme.color.accent}, #facc15, ${theme.color.accent}, transparent)`,
            boxShadow: `0 0 20px rgba(249,115,22,0.80)`,
          }}
        />

        {/* Decorative scattered dots */}
        {[
          { top: "12%", left: "8%", size: 5, color: "#f97316" },
          { top: "8%", left: "78%", size: 4, color: "#facc15" },
          { top: "22%", left: "90%", size: 3, color: "#fb923c" },
          { top: "35%", left: "5%", size: 3, color: "#facc15" },
          { top: "60%", left: "88%", size: 4, color: "#f97316" },
          { top: "72%", left: "6%", size: 5, color: "#facc15" },
        ].map((d, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              top: d.top,
              left: d.left,
              width: `${d.size}px`,
              height: `${d.size}px`,
              borderRadius: "50%",
              background: d.color,
              boxShadow: `0 0 ${d.size * 2}px ${d.color}`,
              pointerEvents: "none",
            }}
          />
        ))}

        <div
          style={{
            padding: "32px 28px 28px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "18px",
            width: "100%",
            position: "relative",
          }}
        >
          {/* Trophy icon */}
          <div
            className="trophy-float"
            style={{
              width: "76px",
              height: "76px",
              borderRadius: "20px",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Trophy size={40} color="#fff" strokeWidth={1.5} />
          </div>

          {/* Title */}
          <div style={{ textAlign: "center" }}>
            <h1
              style={{
                fontFamily: theme.font.family,
                fontWeight: 900,
                fontSize: "clamp(24px,7vw,34px)",
                background: `linear-gradient(90deg, ${theme.color.playFrom}, #facc15, ${theme.color.playFrom})`,
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                lineHeight: 1,
                marginBottom: "8px",
                animation: "prizeShine 2.5s linear infinite",
              }}
            >
              {iWon
                ? "Bingo Master!"
                : winnerName === "Nobody"
                  ? "No Winner!"
                  : `${winnerName} Won!`}
            </h1>
            <p
              style={{
                fontFamily: theme.font.bodyFamily,
                fontSize: "12px",
                color: theme.color.textMuted,
                lineHeight: 1.55,
                maxWidth: "240px",
                margin: "0 auto",
              }}
            >
              {iWon
                ? "You've successfully cleared your cartela and claimed the ultimate prize!"
                : `${winnerName} completed their cartela first. Better luck next round!`}
            </p>
          </div>

          {/* Grand prize row */}
          <div
            style={{
              width: "100%",
              padding: "14px 18px",
              borderRadius: "14px",
              background: "rgba(249,115,22,0.10)",
              border: `1px solid rgba(249,115,22,0.25)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  background: `linear-gradient(135deg,${theme.color.playFrom},${theme.color.playTo})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 0 12px rgba(249,115,22,0.50)`,
                }}
              >
                <Coins size={18} color="#fff" />
              </div>
              <div>
                <div
                  style={{
                    fontFamily: theme.font.bodyFamily,
                    fontSize: "9px",
                    fontWeight: 600,
                    color: theme.color.textDim,
                    textTransform: "uppercase",
                    letterSpacing: "0.16em",
                    marginBottom: "2px",
                  }}
                >
                  {iWon ? "Grand Prize" : "Winner's Prize"}
                </div>
                <div
                  style={{
                    fontFamily: theme.font.family,
                    fontWeight: 900,
                    fontSize: "22px",
                    color: "#fff",
                    letterSpacing: "-0.01em",
                    fontVariantNumeric: "tabular-nums",
                    textShadow: `0 0 16px rgba(249,115,22,0.55)`,
                  }}
                >
                  ₿ {prize}
                  <span
                    style={{
                      fontFamily: theme.font.bodyFamily,
                      fontWeight: 700,
                      fontSize: "12px",
                      color: theme.color.accentLight,
                      marginLeft: "5px",
                    }}
                  >
                    BIRR
                  </span>
                </div>
              </div>
            </div>
            <span
              style={{
                fontFamily: theme.font.bodyFamily,
                fontWeight: 700,
                fontSize: "11px",
                color: "#22c55e",
                background: "rgba(34,197,94,0.12)",
                border: "1px solid rgba(34,197,94,0.28)",
                borderRadius: "9999px",
                padding: "4px 10px",
              }}
            >
              WON
            </span>
          </div>

          {/* Coins + XP row */}
          {iWon && (
            <div style={{ display: "flex", gap: "10px", width: "100%" }}>
              {[
                {
                  Icon: Coins,
                  label: "Bingo Coins",
                  value: `+${coins}`,
                  color: "#f97316",
                  bg: "rgba(249,115,22,0.10)",
                  border: "rgba(249,115,22,0.22)",
                },
                {
                  Icon: TrendingUp,
                  label: "XP Earned",
                  value: `+${xp}`,
                  color: "#a78bfa",
                  bg: "rgba(167,139,250,0.10)",
                  border: "rgba(167,139,250,0.22)",
                },
              ].map(({ Icon, label, value, color, bg, border }) => (
                <div
                  key={label}
                  style={{
                    flex: 1,
                    padding: "12px 10px",
                    borderRadius: "14px",
                    background: bg,
                    border: `1px solid ${border}`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "9px",
                      background: `rgba(255,255,255,0.06)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon size={16} color={color} />
                  </div>
                  <span
                    style={{
                      fontFamily: theme.font.bodyFamily,
                      fontSize: "9px",
                      fontWeight: 600,
                      color: theme.color.textDim,
                      textTransform: "uppercase",
                      letterSpacing: "0.14em",
                    }}
                  >
                    {label}
                  </span>
                  <span
                    style={{
                      fontFamily: theme.font.family,
                      fontWeight: 900,
                      fontSize: "18px",
                      color,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>
          )}
          {/* Countdown bar */}
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontFamily: theme.font.bodyFamily,
                fontSize: "10px",
                color: theme.color.textDim,
              }}
            >
              <span>
                {iWon ? "Returning to card selection…" : "Starting new round…"}
              </span>
              <span style={{ color: theme.color.accentLight, fontWeight: 700 }}>
                {countdown}s
              </span>
            </div>
            <div
              style={{
                height: "4px",
                borderRadius: "9999px",
                background: "rgba(255,255,255,0.08)",
                overflow: "hidden",
              }}
            >
              <div
                key={open ? "running" : "stopped"}
                style={{
                  height: "100%",
                  borderRadius: "9999px",
                  background: `linear-gradient(90deg,${theme.color.playFrom},#facc15)`,
                  boxShadow: `0 0 8px rgba(249,115,22,0.6)`,
                  animation: open ? "countdownBar 3s linear forwards" : "none",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── AutoToggle ───────────────────────────────────────────────────────────────

function AutoToggle({ on, onToggle }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span
        style={{
          fontFamily: theme.font.bodyFamily,
          fontWeight: 600,
          fontSize: "11px",
          color: theme.color.textMuted,
          textTransform: "uppercase",
          letterSpacing: "0.14em",
        }}
      >
        Auto
      </span>
      <button
        onClick={onToggle}
        aria-label="Toggle auto mark"
        style={{
          width: "46px",
          height: "25px",
          borderRadius: "9999px",
          background: on
            ? `linear-gradient(90deg,${theme.color.playFrom},${theme.color.playTo})`
            : "rgba(255,255,255,0.12)",
          border: "none",
          cursor: "pointer",
          position: "relative",
          transition: "background 0.25s ease",
          boxShadow: on ? `0 0 12px ${theme.color.accentGlow}` : "none",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: "4px",
            left: on ? "23px" : "4px",
            width: "17px",
            height: "17px",
            borderRadius: "50%",
            background: "#fff",
            transition: "left 0.22s ease",
            boxShadow: "0 1px 4px rgba(0,0,0,0.5)",
          }}
        />
      </button>
    </div>
  );
}

// ─── Voice MP3 loader (CRA / Webpack require.context) ────────────────────────
// require.context is Webpack's build-time folder scanner —
// the CRA equivalent of Vite's import.meta.glob.
//
// Files must be at:  src/assets/voices/b1.mp3 … o75.mp3
// Adjust the path below if your file lives elsewhere in src/.

function buildVoiceMap() {
  const map = {};
  try {
    // require.context(dir, useSubdirs, regexp)
    // Path is relative to THIS file's location.
    const ctx = require.context(
      "../assets/voices", // relative to src/components/ (adjust if needed)
      false, // don't recurse
      /\.mp3$/, // only .mp3 files
    );
    ctx.keys().forEach((key) => {
      // key = "./g54.mp3"
      const filename = key.replace("./", "");
      const match = filename.match(/^([a-z]+)(\d+)\.mp3$/i);
      if (match) {
        const num = parseInt(match[2], 10);
        map[num] = ctx(key); // resolved public URL
      }
    });
  } catch (e) {
    console.warn("Voice files could not be loaded:", e.message);
  }
  return map;
}

const VOICE_MAP = buildVoiceMap();

// Pre-create one Audio element per number so playback is instant mid-game
const AUDIO_CACHE = {};
Object.entries(VOICE_MAP).forEach(([num, url]) => {
  const audio = new Audio(url);
  audio.preload = "auto";
  AUDIO_CACHE[Number(num)] = audio;
});
let bingoAudioUrl = null;
try {
  bingoAudioUrl = require("../assets/voices/bingo.mp3");
} catch (e) {
  console.warn("bingo.mp3 not found in assets/voices");
}
const BINGO_AUDIO = bingoAudioUrl ? new Audio(bingoAudioUrl) : null;
if (BINGO_AUDIO) BINGO_AUDIO.preload = "auto";

export default function BingoGamePage() {
  const [userMark, setUserMark] = useState(new Set());
  const [isAuto, setIsAuto] = useState(true);
  const [claimed, setClaimed] = useState(false);
  const [showLeave, setShowLeave] = useState(false);
  const [showWin, setShowWin] = useState(false);
  const [winPayload, setWinPayload] = useState(null);
  const [iWon, setIWon] = useState(false);
  const { user } = useAuth();

  const { soundMuted, toggleSound } = useAuth();
  const voiceOn = !soundMuted; // derive directly, single source of truth
  const {
    activeRoom,
    myCartelaDetails,
    leaveGame,
    isSpectator,
    initialLiveState,
    lobbyPlayerCount,
    requiredLines,
    returnToSelection,
  } = useGame();
  const hasMultipleCards = myCartelaDetails.length > 1;

  const [claimedCards, setClaimedCards] = useState(new Set());
  const myCartela = myCartelaDetails?.[0] ?? null; // first card is the "active" one for now
  const myGrid = myCartela?.grid ?? BLANK_GRID;
  const hasMyCard = myCartelaDetails.length > 0;
  const showSpectatorView = isSpectator || !hasMyCard;
  const [called, setCalled] = useState(
    () => new Set(initialLiveState?.called ?? []),
  );
  // bingo is true if ANY of the user's cards has a winning pattern
  const effectiveMarked = isAuto ? called : new Set([...userMark]);

  // const bingo = myCartelaDetails.some((c) => hasBingo(c.grid, effectiveMarked));
  // const btnEnabled =
  //   !isAuto && bingo && myCartelaDetails.some((c) => !claimedCards.has(c.id));
  const bingo = myCartelaDetails.some((c) =>
    hasBingo(c.grid, effectiveMarked, requiredLines),
  );
  const btnEnabled =
    !isAuto && bingo && myCartelaDetails.some((c) => !claimedCards.has(c.id));
  // ...
  // Real game data derived from the active room
  const gameId = activeRoom?.id
    ? `#${activeRoom.id.slice(-6).toUpperCase()}`
    : "#—";
  const gameRoomName = activeRoom?.name ?? "Game";
  const gameStake = activeRoom?.stake
    ? `${parseFloat(activeRoom.stake).toFixed(0)} BIRR`
    : "—";
  const gamePot = activeRoom?.pot_size
    ? `${parseFloat(activeRoom.pot_size).toFixed(0)} BIRR`
    : "—";
  const gamePlayers = lobbyPlayerCount ? String(lobbyPlayerCount) : "—";

  const [history, setHistory] = useState(() => initialLiveState?.called ?? []);
  const [current, setCurrent] = useState(
    () => initialLiveState?.current ?? null,
  );
  // ── Play number audio from MP3 ─────────────────────────────────────────────
  const speakNumber = useCallback(
    (n) => {
      // Stop whatever is currently playing
      if (currentAudio.current) {
        currentAudio.current.pause();
        currentAudio.current.currentTime = 0;
      }

      if (!voiceOn) return;

      const audio = AUDIO_CACHE[n];
      if (!audio) return; // file not found — silent fallback

      // Clone so overlapping calls don't fight each other
      const clip = audio.cloneNode();
      clip.volume = 1;

      currentAudio.current = clip;

      // play() returns a Promise — catch the NotAllowedError on mobile
      // (happens if no user gesture has occurred yet; in practice the
      //  game's START button press is the required gesture)
      clip.play().catch((err) => {
        // NotAllowedError: autoplay blocked — user hasn't interacted yet
        // DOMException: ignore silently
        console.warn("Audio play blocked:", err.message);
      });
      return () => {
        if (currentAudio.current) {
          currentAudio.current.pause();
          currentAudio.current = null;
        }
      };
    },

    [voiceOn],
  );
  const playBingoAnnouncement = useCallback(() => {
    if (!voiceOn || !BINGO_AUDIO) return;

    if (currentAudio.current) {
      currentAudio.current.pause();
      currentAudio.current.currentTime = 0;
    }

    const clip = BINGO_AUDIO.cloneNode();
    clip.volume = 1;
    currentAudio.current = clip;
    clip.play().catch((err) => {
      console.warn("Bingo audio play blocked:", err.message);
    });
  }, [voiceOn]);
  useEffect(() => {
    const handleNumberCalled = ({ number }) => {
      setCurrent(number);
      setCalled((s) => new Set([...s, number]));
      setHistory((h) => [...h, number]);
      speakNumber(number);
    };
    const handleBingoConfirmed = (payload) => {
      playBingoAnnouncement(); // ← new line

      setWinPayload(payload);
      setIWon(
        payload.winnerId != null &&
          String(payload.winnerId) === String(user?.id),
      );
      setShowWin(true);
    };
    const handleBingoRejected = (payload) => {
      console.warn("Bingo rejected:", payload?.reason);
      setClaimed(false); // let them try again — e.g. a mis-tap
    };

    // const handleBingoRejected = (payload) => {
    //   console.warn("Bingo rejected:", payload?.reason);
    // };
    socketService.on("number_called", handleNumberCalled);
    socketService.on("bingo_confirmed", handleBingoConfirmed);
    socketService.on("bingo_rejected", handleBingoRejected);
    return () => {
      socketService.off("number_called", handleNumberCalled);
      socketService.off("bingo_confirmed", handleBingoConfirmed);
      socketService.off("bingo_rejected", handleBingoRejected);
    };
  }, [activeRoom, speakNumber]);

  const handleBingo = () => {
    if (!btnEnabled) return;
    attemptClaim();
  };
  const handleLeave = () => {
    leaveGame(); // from GameContext — emits leave_room AND resets app state
  };
  // ...
  const timer = useRef(null);
  const currentAudio = useRef(null); // track the playing Audio node

  useEffect(() => {
    injectTheme();
    injectStyles();
  }, []);

  // Stop audio when muted mid-game
  useEffect(() => {
    if (!voiceOn && currentAudio.current) {
      currentAudio.current.pause();
      currentAudio.current.currentTime = 0;
    }
  }, [voiceOn]);

  const handleMark = useCallback(
    (num) => {
      if (isAuto) return;
      setUserMark((p) => new Set([...p, num]));
    },
    [isAuto],
  );

  const toggleAuto = () => {
    if (hasMultipleCards) return; // Cannot disable auto with 2 cards
    setIsAuto((a) => !a);
    setClaimed(false);
    setUserMark(new Set());
  };

  // effective marked set for bingo check

  // Auto mode: detect bingo automatically and show win popup
  const attemptClaim = useCallback(() => {
    if (!activeRoom || myCartelaDetails.length === 0) return;

    for (const cartela of myCartelaDetails) {
      if (claimedCards.has(cartela.id)) continue;
      const cardBingo = hasBingo(cartela.grid, effectiveMarked);
      if (cardBingo) {
        setClaimedCards((prev) => new Set([...prev, cartela.id]));
        socketService.claimBingo(
          activeRoom.id,
          cartela.id,
          Array.from(effectiveMarked),
        );
      }
    }
  }, [activeRoom, myCartelaDetails, claimedCards, effectiveMarked]);

  useEffect(() => {
    if (isAuto && bingo && !claimed) attemptClaim();
  }, [bingo, isAuto, claimed, attemptClaim]);

  // Called when 3s countdown ends — go back to cartela selection
  // const handleWinDone = useCallback(() => {
  //   setShowWin(false);
  //   setCalled(new Set());
  //   setHistory([]);
  //   setCurrent(null);
  //   setUserMark(new Set());
  //   setClaimed(false);
  //   setClaimedCards(new Set());
  //   leaveGame(); // Goes back to lobby cleanly without page reload
  // }, [leaveGame]);
  const handleWinDone = useCallback(() => {
    setShowWin(false);
    setCalled(new Set());
    setHistory([]);
    setCurrent(null);
    setUserMark(new Set());
    setClaimed(false);
    setClaimedCards(new Set());
    returnToSelection(); // <-- was leaveGame()
  }, [returnToSelection]);

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        background: theme.color.bgBase,
        display: "flex",
        flexDirection: "column",
        fontFamily: theme.font.bodyFamily,
      }}
    >
      {/* ── STAT BAR ───────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          padding: "5 clamp(16px,5vw,40px) 14px",
        }}
      >
        <StatPill
          icon={Hash}
          label="Game ID"
          value={gameId}
          accent={theme.color.textMuted}
        />
        <StatPill
          icon={Users}
          label="Players"
          value={gamePlayers}
          accent="#a78bfa"
        />
        <StatPill
          icon={Coins}
          label="Stake"
          value={gameStake}
          accent={theme.color.accentLight}
        />
        <StatPill icon={Trophy} label="Pot" value={gamePot} accent="#facc15" />
      </div>

      {/* ── SCROLLABLE BODY ────────────────────────────────── */}
      <div
        className="noscroll"
        style={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          paddingBottom: "100px" /* room for sticky footer */,
        }}
      >
        {/* ── CALLING HERO ─────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            padding: "10px clamp(16px,5vw,40px) 0",
          }}
        >
          {/* Top row: card info + auto toggle — hidden for spectators */}
          {!showSpectatorView ? (
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span
                    style={{
                      fontFamily: theme.font.family,
                      fontWeight: 900,
                      fontSize: "15px",
                      color: theme.color.textPrimary,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    My Card
                  </span>
                  <span
                    style={{
                      fontFamily: theme.font.bodyFamily,
                      fontWeight: 700,
                      fontSize: "12px",
                      color: theme.color.accentLight,
                    }}
                  >
                    {myCartelaDetails[0]?.card_number ?? "—"}
                  </span>
                </div>
                <span
                  style={{
                    fontFamily: theme.font.bodyFamily,
                    fontSize: "10px",
                    color: theme.color.textDim,
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                  }}
                >
                  {isAuto
                    ? "Auto-marking active"
                    : "Tap called numbers to mark"}
                </span>
              </div>
              {!hasMultipleCards && (
                <AutoToggle on={isAuto} onToggle={toggleAuto} />
              )}
              {hasMultipleCards && (
                <span
                  style={{
                    fontSize: "11px",
                    fontFamily: theme.font.bodyFamily,
                    color: theme.color.textDim,
                    background: "rgba(255,255,255,0.04)",
                    border: `1px solid ${theme.color.borderCard}`,
                    borderRadius: theme.radius.full,
                    padding: "4px 10px",
                  }}
                >
                  AUTO LOCKED
                </span>
              )}
            </div>
          ) : (
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  fontFamily: theme.font.bodyFamily,
                  fontWeight: 700,
                  fontSize: "11px",
                  color: "#a78bfa",
                  background: "rgba(167,139,250,0.12)",
                  border: "1px solid rgba(167,139,250,0.28)",
                  borderRadius: theme.radius.full,
                  padding: "5px 14px",
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                }}
              >
                👁 Spectating
              </span>
            </div>
          )}

          {/* Calling dial centred */}
          <CallingHero
            current={current}
            voiceOn={voiceOn}
            onToggleVoice={toggleSound}
          />
        </div>

        {/* ── PREVIOUS NUMBERS ─────────────────────────────── */}
        <PreviousStrip history={history} current={current} />

        {/* ── DIVIDER ──────────────────────────────────────── */}
        <div
          style={{
            margin: "0 clamp(16px,5vw,40px)",
            height: "1px",
            background: `linear-gradient(to right,transparent,${theme.color.borderDivide},transparent)`,
          }}
        />

        {/* ── BINGO CARD — hidden for spectators ───────────── */}
        {!showSpectatorView && (
          <div style={{ padding: `0 clamp(0px,1vw,24px)` }}>
            {/* Status hint above card */}
            {!isAuto && (
              <div
                style={{
                  margin: "0 16px 10px",
                  padding: "9px 14px",
                  borderRadius: theme.radius.md,
                  background: bingo
                    ? "rgba(34,197,94,0.10)"
                    : "rgba(255,255,255,0.03)",
                  border: `1px solid ${bingo ? "rgba(34,197,94,0.30)" : theme.color.borderCard}`,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: bingo ? "#22c55e" : theme.color.textDim,
                    boxShadow: bingo ? "0 0 8px rgba(34,197,94,0.70)" : "none",
                  }}
                />
                <span
                  style={{
                    fontFamily: theme.font.bodyFamily,
                    fontSize: "11px",
                    color: bingo ? "#22c55e" : theme.color.textDim,
                  }}
                >
                  {claimed
                    ? "🎉 BINGO claimed! Waiting for verification…"
                    : bingo
                      ? "You have a BINGO! Tap the button to claim."
                      : "Tap orange cells to mark your card."}
                </span>
              </div>
            )}
            {isAuto &&
              (hasMultipleCards ? (
                <div
                  style={{
                    margin: "0 16px 10px",
                    padding: "9px 14px",
                    borderRadius: theme.radius.md,
                    background: "rgba(249,115,22,0.06)",
                    border: `1px solid ${theme.color.accentRing}`,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Zap size={13} color={theme.color.accentLight} />
                  <span
                    style={{
                      fontFamily: theme.font.bodyFamily,
                      fontSize: "11px",
                      color: theme.color.textMuted,
                    }}
                  >
                    Auto mode — locked (2 cards selected)
                  </span>
                </div>
              ) : (
                <div
                  style={{
                    margin: "0 16px 10px",
                    padding: "9px 14px",
                    borderRadius: theme.radius.md,
                    background: "rgba(249,115,22,0.06)",
                    border: `1px solid ${theme.color.accentRing}`,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Zap size={13} color={theme.color.accentLight} />
                  <span
                    style={{
                      fontFamily: theme.font.bodyFamily,
                      fontSize: "11px",
                      color: theme.color.textMuted,
                    }}
                  >
                    Auto mode — numbers are marked automatically as they're
                    called.
                  </span>
                </div>
              ))}

            {/* <BingoCard
              called={called}
              isAuto={isAuto}
              userMarked={userMark}
              onMark={handleMark}
              grid={myGrid}
            /> */}
            {myCartelaDetails.map((cartela, idx) => (
              <div
                key={cartela.id}
                style={{
                  marginBottom: idx < myCartelaDetails.length - 1 ? "14px" : 0,
                }}
              >
                {myCartelaDetails.length > 1 && (
                  <div
                    style={{
                      padding: "0 16px 6px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: theme.font.family,
                        fontWeight: 900,
                        fontSize: "13px",
                        color: theme.color.accentLight,
                      }}
                    >
                      Card {idx + 1} — #{cartela.card_number}
                    </span>
                    {claimedCards.has(cartela.id) && (
                      <span
                        style={{
                          fontSize: "10px",
                          background: "rgba(34,197,94,0.15)",
                          border: "1px solid rgba(34,197,94,0.30)",
                          color: "#22c55e",
                          borderRadius: "9999px",
                          padding: "2px 8px",
                          fontFamily: theme.font.bodyFamily,
                          fontWeight: 700,
                        }}
                      >
                        CLAIMED ✓
                      </span>
                    )}
                  </div>
                )}
                <BingoCard
                  called={called}
                  isAuto={isAuto}
                  userMarked={userMark}
                  onMark={handleMark}
                  grid={cartela.grid}
                />
              </div>
            ))}
          </div>
        )}
        {/* ── REFERENCE BOARD ──────────────────────────────── */}
        <div style={{ padding: `0 clamp(0px,1vw,24px)` }}>
          <ReferenceBoard called={called} />
        </div>
      </div>

      {/* ── STICKY FOOTER ──────────────────────────────────── */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "12px clamp(16px,5vw,40px) clamp(16px,4vw,28px)",
          borderTop: `1px solid ${theme.color.border}`,
          background: "rgba(13,13,18,0.95)",
          backdropFilter: "blur(16px)",
        }}
      >
        {/* Leave */}
        <button
          onClick={() => setShowLeave(true)}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "3px",
            padding: "9px 16px",
            borderRadius: theme.radius.md,
            flexShrink: 0,
            background: "rgba(255,255,255,0.05)",
            border: `1px solid ${theme.color.borderCard}`,
            cursor: "pointer",
            transition: theme.transition.base,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(239,68,68,0.10)";
            e.currentTarget.style.borderColor = "rgba(239,68,68,0.28)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.05)";
            e.currentTarget.style.borderColor = theme.color.borderCard;
          }}
        >
          <LogOut size={16} color={theme.color.textMuted} />
          <span
            style={{
              fontFamily: theme.font.bodyFamily,
              fontWeight: 600,
              fontSize: "9px",
              color: theme.color.textDim,
              textTransform: "uppercase",
              letterSpacing: "0.12em",
            }}
          >
            Leave
          </span>
        </button>

        {/* Refresh */}
        <button
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "3px",
            padding: "9px 16px",
            borderRadius: theme.radius.md,
            flexShrink: 0,
            background: "rgba(255,255,255,0.05)",
            border: `1px solid ${theme.color.borderCard}`,
            cursor: "pointer",
            transition: theme.transition.base,
          }}
        >
          <RefreshCw size={16} color={theme.color.textMuted} />
          <span
            style={{
              fontFamily: theme.font.bodyFamily,
              fontWeight: 600,
              fontSize: "9px",
              color: theme.color.textDim,
              textTransform: "uppercase",
              letterSpacing: "0.12em",
            }}
          >
            Refresh
          </span>
        </button>

        {/* BINGO — hidden for spectators */}
        {!showSpectatorView ? (
          <button
            onClick={handleBingo}
            disabled={!btnEnabled}
            className={btnEnabled ? "bingo-pulse" : ""}
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              padding: "15px 20px",
              borderRadius: theme.radius.lg,
              background: btnEnabled
                ? `linear-gradient(135deg,${theme.color.playFrom},${theme.color.playTo})`
                : "rgba(255,255,255,0.06)",
              border: "none",
              cursor: btnEnabled ? "pointer" : "not-allowed",
              fontFamily: theme.font.family,
              fontWeight: 900,
              fontSize: "clamp(17px,4vw,24px)",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: btnEnabled ? "#fff" : theme.color.textDim,
              transition: "all 0.25s ease",
              opacity: isAuto ? 0.35 : 1,
            }}
          >
            <Flame
              size={20}
              color={btnEnabled ? "#fff" : theme.color.textDim}
              fill={btnEnabled ? "#fff" : "none"}
            />
            {claimed ? "Claimed! 🎉" : isAuto ? "Auto Mode" : "Bingo!"}
          </button>
        ) : (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "15px 20px",
              borderRadius: theme.radius.lg,
              background: "rgba(167,139,250,0.08)",
              border: "1px solid rgba(167,139,250,0.22)",
            }}
          >
            <span
              style={{
                fontFamily: theme.font.bodyFamily,
                fontWeight: 700,
                fontSize: "13px",
                color: "#a78bfa",
                letterSpacing: "0.10em",
                textTransform: "uppercase",
              }}
            >
              👁 Spectating
            </span>
          </div>
        )}
      </div>
      {/* ── LEAVE CONFIRMATION DIALOG ──────────────────────── */}
      <LeaveConfirmDialog
        gameStake={gameStake}
        open={showLeave}
        onCancel={() => setShowLeave(false)}
        onConfirm={() => {
          clearInterval(timer.current);
          setShowLeave(false);
          leaveGame();
        }}
      />

      {/* ── WIN POPUP ──────────────────────────────────────── */}
      <WinPopup
        open={showWin}
        iWon={iWon}
        winnerName={winPayload?.winnerName ?? ""}
        prize={String(winPayload?.prize ?? "0")}
        coins={iWon ? 120 : 0}
        xp={iWon ? 450 : 0}
        onDone={handleWinDone}
      />
    </div>
  );
}
